import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Users, CheckCircle, XCircle, Lock, User } from 'lucide-react';
import { familyService } from '@services/familyService';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import toast from 'react-hot-toast';

const acceptInviteSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres').max(50).optional().or(z.literal('')),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres').optional().or(z.literal('')),
});

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(acceptInviteSchema),
  });

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Token de convite inválido ou ausente.');
    }
  }, [token]);

  const onSubmit = async (data) => {
    if (!token) return;
    setIsLoading(true);
    try {
      await familyService.acceptInvite({
        token,
        ...(data.name ? { name: data.name } : {}),
        ...(data.password ? { password: data.password } : {}),
      });
      setStatus('success');
      toast.success('Convite aceito com sucesso!');
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Erro ao aceitar convite';
      setStatus('error');
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Convite Aceito!
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Você foi adicionado ao grupo familiar com sucesso. Faça login para acessar sua conta.
        </p>
        <Button fullWidth onClick={() => navigate('/login')}>
          Ir para o Login
        </Button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <XCircle size={32} className="text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Convite Inválido
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          {errorMessage || 'Este convite é inválido, expirou ou já foi utilizado.'}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
        >
          Voltar ao login
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-6">
        <Users size={28} className="text-primary-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Aceitar Convite Familiar
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Você foi convidado para um grupo familiar. Se ainda não tem conta, preencha seus dados abaixo.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Nome (opcional, para novos usuários)"
          type="text"
          placeholder="Seu nome completo"
          leftIcon={<User size={18} />}
          error={errors.name?.message}
          {...register('name')}
        />

        <Input
          label="Senha (opcional, para novos usuários)"
          type="password"
          placeholder="Crie uma senha"
          leftIcon={<Lock size={18} />}
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" fullWidth isLoading={isLoading}>
          Aceitar Convite
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
        Já tem conta?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Fazer login
        </Link>
      </p>
    </div>
  );
};

export default AcceptInvite;
